import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'

import { now as serverNow } from '~/models/clock'
import { startOfTodayJst } from '~/models/dateTime'
import {
  defineFilterCodec,
  isoDate,
  oneOf,
  optional,
  protoEnumValues,
} from '~/models/filterCodec'
import { computeDefaultTargetDate } from '~/pages/DemandForecastList/models/tabDefinitions'

/**
 * 需要想定一覧のフィルタバー保持 key。
 *
 * AC-SC-031 [P0] (`docs/specs/common/shared-components.md`) が保存媒体まで
 * localStorage と規定する (タブ生存期間で消える sessionStorage は「再訪時に復元」を
 * 満たさない)。
 */
export const DEMAND_FORECAST_LIST_FILTERS_STORAGE_KEY =
  'ebalance-demand-forecast-list-filters'

/**
 * 需要想定一覧のフィルタバー state (in-memory 形状)。画面遷移・再訪をまたいで
 * 保持する対象 (AC-SC-031 / issue #6684)。
 *
 * 対象はフィルタバー上の 3 つ (対象日 / エリア / ステータス) に限る。
 * **タブ (単日 / 期間) は含めない** — フィルタバー外の別軸 UI で、需要調達計画一覧が
 * 計画期間タブを除外しているのと同じ扱い
 * (`pages/ProcurementPlanList/models/persistedFilters.ts`)。
 */
export type DemandForecastListFilters = {
  targetDate: Date
  /** undefined = 全エリア。 */
  selectedArea: AreaCode | undefined
  /** undefined = すべてのステータス。 */
  selectedStatus: WorkflowCaseStatus | undefined
}

/**
 * フィルタ既定値。対象日は単日タブの基点日 (= 今日)、他は絞り込みなし。
 *
 * `now` を注入可能にするのは 2 つの時計の混在を避けるため。
 */
export function defaultDemandForecastListFilters(
  now: Date = serverNow(),
): DemandForecastListFilters {
  return {
    // 基準日は **JST の今日**。 端末ローカルの暦日で丸めると、 非 JST 端末では
    // 日付差のある時間帯に 1 日ズレた一覧を出す (#6870)。
    targetDate: computeDefaultTargetDate(startOfTodayJst(now), 0),
    selectedArea: undefined,
    selectedStatus: undefined,
  }
}

/**
 * 保存形式は `{ targetDate: 'YYYY-MM-DD', selectedArea: number | null,
 * selectedStatus: number | null }`。壊れた値・stale enum は項目単位で既定へ落ちる。
 */
export const demandForecastListFiltersCodec =
  defineFilterCodec<DemandForecastListFilters>({
    defaults: defaultDemandForecastListFilters,
    fields: {
      targetDate: isoDate(),
      selectedArea: optional(oneOf(protoEnumValues(AreaCode)), {
        wire: 'null',
      }),
      selectedStatus: optional(oneOf(protoEnumValues(WorkflowCaseStatus)), {
        wire: 'null',
      }),
    },
  })
